import { useNavigate } from "react-router-dom";
import {
  physiotherapyBenefits,
  massageBenefits,
  chiropracticBenefits,
  acupunctureBenefits,
} from "../../../data/servicesData";
import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useSearchLocation } from "../../../store/searchLocation"

interface OtherServicesData {
  title: string;
  image: string;
  url: string;
  mini_desc?: string;
}

interface ServiceTitleProps {
  serviceTitle: string;
  serviceImage: string | undefined;
  serviceDescData: string[] | undefined;
  otherServicesData: OtherServicesData[];
}

export default function ServiceMainComponent({
  serviceTitle,
  serviceImage,
  serviceDescData,
  otherServicesData,
}: ServiceTitleProps) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>("what-is");
  const { updateSearchLocation } = useSearchLocation()

  const allBenefits: any = {
    Physiotherapy: physiotherapyBenefits,
    "Massage Therapy": massageBenefits,
    Acupuncture: acupunctureBenefits,
    "Chiropractic Care": chiropracticBenefits,
  };

  const benefitsToRender = allBenefits[serviceTitle] || [];

  const serviceMappingToIds:any = {
    "physiotherapy":1,
    "chiropractic care":2,
    "acupuncture":3,
    "massage therapy":4,
  }

  // Scroll tracking
  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-50% 0px -40% 0px" },
    );
    sections.forEach((s) => observer.observe(s));
    return () => sections.forEach((s) => observer.unobserve(s));
  }, []);

  return (
    <main
      className="text-[#2f2f2f] relative flex flex-col lg:flex-row max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-16 bg-background text-foreground"
      style={{ scrollBehavior: "smooth" }}
    >
      {/* Left Main Content */}
      <div className="lg:w-[80%] w-full lg:pr-16 space-y-24">
        {/* 🌟 Intro Section */}
        <section
          id="book-an-appointment"
          className="scroll-mt-32 bg-gradient-to-br from-teal-50 to-white border border-teal-100 rounded-3xl p-10 sm:p-12 shadow-md transition-all"
        >
          <div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-teal-700 mb-4 leading-snug">
              Discover the Power of {serviceTitle}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Experience how {serviceTitle} can rejuvenate your body and mind.
              Learn about its benefits and explore similar therapies designed to
              uplift your well-being.
            </p>
            <button
              onClick={() =>{
                updateSearchLocation({
                  userSelectedService:serviceMappingToIds[serviceTitle.trim().toLocaleLowerCase()]
                })
                navigate('/serviceList')
              }
              }
              className="bg-teal-600 text-white font-medium px-6 py-3 mt-6 rounded-xl shadow-md hover:bg-teal-700 transition-all flex items-center gap-2 disabled:opacity-80"
            >
              Book {serviceTitle}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Section: What is */}
        <section id="what-is" className="scroll-mt-32">
          <h1 className="text-3xl sm:text-4xl mb-10">{serviceTitle}</h1>
          <div className="space-y-5 text-lg leading-relaxed text-[#333333]">
            {serviceDescData?.map((desc, i) => (
              <p key={i}>{desc}</p>
            ))}
          </div>

          {serviceImage && (
            <div className="mt-12 flex justify-center">
              <img
                src={serviceImage}
                alt={serviceTitle}
                className="rounded-3xl shadow-lg object-cover w-full sm:w-[80%] lg:w-[50%] transition-transform duration-500 hover:scale-[1.02]"
              />
            </div>
          )}
        </section>

        {/* Section: Benefits */}
        <section id="benefits" className="scroll-mt-32 space-y-10">
          <h2 className="text-3xl sm:text-4xl">
            Key Benefits of Regular {serviceTitle}
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefitsToRender.map((benefit: any, i: number) => (
              <div
                key={i}
                className="group bg-white border border-gray-200 text-foreground shadow-sm hover:shadow-lg rounded-2xl p-4 transition-all hover:-translate-y-1 hover:border-teal-200"
              >
                <div className="bg-teal-50 rounded-full p-3 inline-flex mb-3 group-hover:bg-teal-100 transition-colors">
                  <benefit.icon className="h-5 w-5 text-teal-700" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-base leading-normal">
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Section: Other Services */}
        <section id="other-services" className="scroll-mt-32 space-y-10">
          <h2 className="text-3xl sm:text-4xl">Explore Our Other Therapies</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherServicesData.map((service, i) => (
              <div
                key={i}
                onClick={() => navigate(`/service/${service.url}`)}
                className="bg-white border border-gray-200 text-foreground shadow-sm hover:shadow-md rounded-2xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1 group"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="object-cover w-full h-48 transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-1 text-gray-800">
                    {service.title}
                  </h3>
                  <p className="text-base text-gray-600">{service.mini_desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Sidebar */}
      <aside className="hidden lg:block lg:w-[20%] sticky top-32 h-fit">
        <div className="relative pl-6">
          {/* Persistent left border */}
          <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gray-200" />

          <h3 className="text-lg font-semibold text-teal-700 mb-6">
            On this page
          </h3>

          <nav className="space-y-3 text-gray-500 relative">
            {[
              { id: "book-an-appointment", label: "Book an appointment" },
              { id: "what-is", label: `What is ${serviceTitle}` },
              { id: "benefits", label: "Benefits" },
              { id: "other-services", label: "Other Therapies" },
            ].map((link) => (
              <button
                key={link.id}
                onClick={() => {
                  const el = document.getElementById(link.id);
                  if (el) {
                    el.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                }}
                className={`block text-left w-full relative pl-4 transition-all
        before:content-[''] before:absolute before:left-[-1.5rem] before:top-0 before:bottom-0
        before:w-[3px] before:h-9 before:transition-all
        ${
          activeSection === link.id
            ? "text-teal-700 font-medium before:bg-teal-600"
            : "hover:text-teal-600 before:bg-transparent"
        }
      `}
              >
                {link.label}
              </button>
            ))}
          </nav>
        </div>
      </aside>
    </main>
  );
}
